import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Avatar } from "@/components/ui/avatar"
import { Sparkline } from "@/components/ui/sparkline"
import { LineChart } from "@/components/ui/line-chart"
import { StackedBarChart } from "@/components/ui/stacked-bar-chart"
import { HeatmapGrid } from "@/components/ui/heatmap-grid"
import { KpiCard } from "@/components/ui/kpi-card"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import type { Ticket } from "@/types/ticket"

// /analytics/sla — SLA Performance dashboard.
//
// Backed by Portal::Analytics::Sla::Presenter. Three-band grid layout:
//   1. KPI row (6 cards)
//   2. Mid band: SLA attainment over time (left) + Idle response breaches sidebar (right)
//   3. Bottom band: Breaches by type / When breaches happen / Team SLA (humans-only) / Tickets at risk
// Every card has its own header with title + per-card meta. Range
// controls live INSIDE the SLA-attainment chart card, not in the page
// header — so different views can plug different controls into the
// same shell. PageHeader carries the "Create report" / "Share" actions.

// ─── Types (camelCase, matches the presenter output) ─────────────────

type RangeKey = "today" | "7d" | "30d" | "90d" | "custom"

type KpiValue = {
  value: number | null
  delta: number | null
  deltaUnit: "pp" | "sec" | "abs"
  goal: number | null
  sparkline: (number | null)[]
}

type AttainmentSeries = {
  dates: string[]
  firstResponsePct: (number | null)[]
  resolutionPct: (number | null)[]
  idleBreaches: number[]
}

type IdleBreachesByDay = {
  dates: string[]
  counts: number[]
}

type BreachesByTypeByDay = {
  dates: string[]
  firstResponse: number[]
  idle: number[]
  resolution: number[]
}

type BreachHeatmap = {
  cells: { dow: number; hour: number; count: number }[]
}

// Humans-only — the presenter no longer ships AI rows. `kind` still
// arrives on the wire as "human" but the FE no longer reads it.
type EntityRow = {
  id: string
  name: string
  avatarUrl?: string | null
  frAttainmentPct: number | null
  resAttainmentPct: number | null
  idleBreaches: number
  avgFirstResponseSeconds: number | null
  convosHandled: number
  sparkline: (number | null)[]
}

export type SlaPerformanceProps = {
  range: { key: RangeKey; from: string; to: string }
  kpis: {
    firstResponseAttainmentPct: KpiValue
    resolutionAttainmentPct: KpiValue
    idleBreachesTotal: KpiValue
    ticketsAtRiskCount: KpiValue
    avgFirstResponseSeconds: KpiValue
    ghostedRatePct: KpiValue
  }
  attainmentSeries: AttainmentSeries
  idleBreachesByDay: IdleBreachesByDay
  breachesByTypeByDay: BreachesByTypeByDay
  breachHeatmap: BreachHeatmap
  entityTable: EntityRow[]
  ticketsAtRisk: Ticket[]
}

// ─── Visual constants ─────────────────────────────────────────────────
//
// Mock palette: first-response = red, resolution = gold/amber,
// idle = grey dashed (visually quieter so the two attainment lines
// dominate the chart). Stacked-bar uses the same hues, so the two
// charts share a vocabulary.

const COLOR_FR   = "#ef4444"
const COLOR_RES  = "#fbbf24"
const COLOR_IDLE = "#9ca3af"

const RANGES: Array<{ key: RangeKey; label: string }> = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7d" },
  { key: "30d",   label: "30d" },
  { key: "90d",   label: "90d" },
  { key: "custom", label: "Custom" },
]

type EntitySortKey =
  | "name"
  | "frAttainmentPct"
  | "resAttainmentPct"
  | "idleBreaches"
  | "avgFirstResponseSeconds"
  | "convosHandled"

// ─── Page ────────────────────────────────────────────────────────────

export default function SlaPerformance(props: SlaPerformanceProps) {
  // Defensive defaults — backend may evolve, page must still render.
  const range = props.range ?? { key: "30d" as RangeKey, from: "", to: "" }
  const kpis = props.kpis ?? ({} as SlaPerformanceProps["kpis"])
  const attainment = props.attainmentSeries ?? { dates: [], firstResponsePct: [], resolutionPct: [], idleBreaches: [] }
  const byType = props.breachesByTypeByDay ?? { dates: [], firstResponse: [], idle: [], resolution: [] }
  const heatmap = props.breachHeatmap ?? { cells: [] }
  const entities = props.entityTable ?? []
  const ticketsAtRisk = props.ticketsAtRisk ?? []

  const priorLabel = priorLabelFor(range.key)

  const [sortKey, setSortKey] = useState<EntitySortKey>("frAttainmentPct")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  function setRange(key: RangeKey) {
    router.visit(`/analytics/sla?range=${key}`, { preserveState: false, preserveScroll: true })
  }

  function toggleSort(key: EntitySortKey) {
    if (key === sortKey) {
      setSortDir(sortDir === "desc" ? "asc" : "desc")
      return
    }
    setSortKey(key)
    setSortDir(key === "avgFirstResponseSeconds" || key === "idleBreaches" ? "asc" : "desc")
  }

  const filteredEntities = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1
    return entities.slice().sort((a, b) => {
      const av = entitySortValue(a, sortKey)
      const bv = entitySortValue(b, sortKey)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir
      return ((av as number) - (bv as number)) * dir
    })
  }, [entities, sortKey, sortDir])

  const frGoal = kpis.firstResponseAttainmentPct?.goal ?? null
  const resGoal = kpis.resolutionAttainmentPct?.goal ?? null

  const totalBreaches = useMemo(() => {
    const fr = sumArr(byType.firstResponse)
    const id = sumArr(byType.idle)
    const re = sumArr(byType.resolution)
    return fr + id + re
  }, [byType])

  const totalBreachesDelta = null  // not on presenter yet — TODO PR2 (separate metric)

  return (
    <AppShell>
      <PageHeader
        eyebrow="Analytics"
        title="SLA Performance"
        subtitle="Track how we're meeting our SLA commitments and where we can improve."
      />

      <div className="space-y-3">
        {/* ─── KPI row ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <KpiCard
            label="First-response SLA attainment"
            kpi={kpis.firstResponseAttainmentPct}
            fillColor={COLOR_FR}
            formatValue={formatPct}
            goalCmp="gte"
            priorLabel={priorLabel}
            footnote={targetFootnote(kpis.firstResponseAttainmentPct?.goal, "gte", formatPct)}
          />
          <KpiCard
            label="Resolution SLA attainment"
            kpi={kpis.resolutionAttainmentPct}
            fillColor={COLOR_RES}
            formatValue={formatPct}
            goalCmp="gte"
            priorLabel={priorLabel}
            footnote={targetFootnote(kpis.resolutionAttainmentPct?.goal, "gte", formatPct)}
          />
          <KpiCard
            label="Idle breaches"
            kpi={kpis.idleBreachesTotal}
            fillColor={COLOR_IDLE}
            formatValue={formatCount}
            goalCmp="lte"
            invertDelta
            showDeltaPct
            sparklineVariant="bar"
            priorLabel={priorLabel}
            footnote={`Idle breaches in range: ${formatCount(kpis.idleBreachesTotal?.value ?? null)}`}
          />
          <KpiCard
            label="Tickets at risk now"
            kpi={kpis.ticketsAtRiskCount}
            fillColor="#ef4444"
            formatValue={formatCount}
            goalCmp="lte"
            invertDelta
            showDeltaPct
            priorLabel={priorLabel}
            footnote="Across all SLA types"
          />
          <KpiCard
            label="Avg. time to first response"
            kpi={kpis.avgFirstResponseSeconds}
            fillColor={COLOR_FR}
            formatValue={formatSeconds}
            goalCmp="lte"
            invertDelta
            priorLabel={priorLabel}
            footnote={targetFootnote(kpis.avgFirstResponseSeconds?.goal, "lte", formatSeconds)}
          />
          <KpiCard
            label="Ghosted rate"
            kpi={kpis.ghostedRatePct}
            fillColor="#ef4444"
            formatValue={formatPct}
            goalCmp="lte"
            invertDelta
            priorLabel={priorLabel}
            footnote="No SLA target"
          />
        </div>

        {/* ─── Mid band: attainment chart, full width ─────────────────── */}
        <div className="grid grid-cols-1 gap-3">
          <Card className="p-5">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-app-text">SLA attainment over time</h2>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs">
                  <LegendDot color={COLOR_FR}   label="First-response SLA attainment %" />
                  <LegendDot color={COLOR_RES}  label="Resolution SLA attainment %" />
                  <LegendDot color={COLOR_IDLE} label="Idle breaches (count)" dashed />
                </div>
              </div>
              <RangeSwitcher activeKey={range.key} onChange={setRange} />
            </div>
            {attainment.dates.length === 0 ? (
              <EmptyState height={300} message="No SLA-tracked tickets in this range." />
            ) : (
              <LineChart
                dates={attainment.dates}
                height={300}
                formatY={(n) => formatPct(n)}
                formatYRight={(n) => Math.round(n).toString()}
                referenceY={frGoal}
                referenceLabel={frGoal != null ? `Target ${formatPct(frGoal)}` : undefined}
                series={[
                  { id: "fr",  label: "First-response %", data: attainment.firstResponsePct, color: COLOR_FR,   yAxisId: "left" },
                  { id: "res", label: "Resolution %",     data: attainment.resolutionPct,    color: COLOR_RES,  yAxisId: "left" },
                  { id: "idle", label: "Idle breaches",   data: attainment.idleBreaches.map((n) => (n ?? 0)),   color: COLOR_IDLE, yAxisId: "right", strokeDasharray: "4 4" },
                ]}
              />
            )}
            <div className="mt-2 text-[11px] text-app-subtle">No SLA target on some tickets.</div>
          </Card>
        </div>

        {/* ─── Bottom band: 4 cards in a 12-col grid ────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">

          {/* Breaches by type — stacked bar */}
          <Card className="p-5 xl:col-span-3 flex flex-col">
            <h2 className="text-base font-semibold text-app-text">Breaches by type</h2>
            <div className="mt-1 text-[11px] uppercase tracking-wide text-app-subtle">Total breaches</div>
            <BigNumberWithDelta
              kpi={{
                value: totalBreaches,
                delta: totalBreachesDelta,
                deltaUnit: "abs",
                goal: null,
                sparkline: [],
              }}
              format={formatCount}
              invertDelta
              showDeltaPct
              priorLabel={priorLabel}
              className="mt-1"
            />
            <div className="mt-3 flex-1 min-h-[180px]">
              {byType.dates.length === 0 || totalBreaches === 0 ? (
                <div className="grid h-full place-items-center text-sm text-app-muted">
                  No breaches in this range.
                </div>
              ) : (
                <StackedBarChart
                  dates={byType.dates}
                  fill
                  series={[
                    { id: "fr",  label: "First-response", data: byType.firstResponse, color: COLOR_FR },
                    { id: "id",  label: "Idle",           data: byType.idle,          color: COLOR_IDLE },
                    { id: "res", label: "Resolution",     data: byType.resolution,    color: COLOR_RES },
                  ]}
                />
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px]">
              <LegendSquare color={COLOR_FR}   label="First-response" />
              <LegendSquare color={COLOR_IDLE} label="Idle" />
              <LegendSquare color={COLOR_RES}  label="Resolution" />
            </div>
            <footer className="mt-2">
              <a href="/tickets?sla_breached=any&since=30d" className="text-[11px] font-medium text-brand-red hover:underline">
                View all breaches →
              </a>
            </footer>
          </Card>

          {/* When breaches happen — heatmap */}
          <Card className="p-5 xl:col-span-3 flex flex-col">
            <h2 className="text-base font-semibold text-app-text">When breaches happen</h2>
            <div className="text-[11px] text-app-subtle">All breach types · Last 30 days</div>
            <div className="mt-3 flex-1">
              <HeatmapGrid
                cells={heatmap.cells}
                bins={6}
                dowStart="mon"
                emptyMessage="No breaches in this range."
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-app-subtle">
              <span>Fewer</span>
              <div className="flex-1 mx-2 h-1.5 rounded-full bg-gradient-to-r from-app-panel2 to-status-error" />
              <span>More</span>
            </div>
          </Card>

          {/* Team SLA performance */}
          <Card className="p-0 xl:col-span-6 flex flex-col overflow-hidden">
            <header className="flex flex-wrap items-center gap-3 p-5 pb-3">
              <div>
                <h2 className="inline-flex items-center gap-1.5 text-base font-semibold text-app-text">
                  Team SLA performance
                  <InfoTooltip side="bottom">
                    <div className="space-y-2">
                      <div>
                        Per-handler SLA scoreboard for human operators. Column abbreviations:
                      </div>
                      <ul className="space-y-1 pl-3 list-disc">
                        <li><strong>Entity</strong> — the human handler.</li>
                        <li><strong>FR %</strong> — first-response SLA attainment.
                          <code className="ml-1 text-app-subtle">met / (met + breached)</code>.</li>
                        <li><strong>Res %</strong> — resolution SLA attainment, same formula.</li>
                        <li><strong>Idle breaches</strong> — count (not a duration) of idle-response breaches across all
                          tickets handled in the range.</li>
                        <li><strong>Avg TTFR</strong> — average time-to-first-response across
                          all tickets handled (seconds → human duration).</li>
                        <li><strong>Tickets</strong> — total tickets handled in the range.</li>
                        <li><strong>Trend</strong> — 7-day FR attainment sparkline, colored
                          green/amber/red against the company goal.</li>
                      </ul>
                      <div className="text-app-muted">
                        Dot color on FR / Res % follows the company attainment goal: green ≥
                        goal, amber within 5pp below, red beyond.
                      </div>
                    </div>
                  </InfoTooltip>
                </h2>
                <div className="text-[11px] text-app-subtle">Last 7 days</div>
              </div>
            </header>
            {filteredEntities.length === 0 ? (
              <div className="p-5 text-sm text-app-muted">
                {entities.length === 0
                  ? "No SLA-tracked tickets in this range."
                  : "No handlers match your filters."}
              </div>
            ) : (
              <div className="flex-1 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-y border-border bg-white/[0.02] text-[11px] uppercase tracking-wide text-app-subtle">
                      <SortHeader label="Entity"   onClick={() => toggleSort("name")}                    active={sortKey === "name"}                    dir={sortDir} align="left" />
                      <SortHeader label="FR %"     onClick={() => toggleSort("frAttainmentPct")}         active={sortKey === "frAttainmentPct"}         dir={sortDir} align="right" />
                      <SortHeader label="Res %"    onClick={() => toggleSort("resAttainmentPct")}        active={sortKey === "resAttainmentPct"}        dir={sortDir} align="right" />
                      <SortHeader label="Idle breaches" onClick={() => toggleSort("idleBreaches")}            active={sortKey === "idleBreaches"}            dir={sortDir} align="right" />
                      <SortHeader label="Avg TTFR" onClick={() => toggleSort("avgFirstResponseSeconds")} active={sortKey === "avgFirstResponseSeconds"} dir={sortDir} align="right" />
                      <SortHeader label="Tickets"  onClick={() => toggleSort("convosHandled")}           active={sortKey === "convosHandled"}           dir={sortDir} align="right" />
                      <th className="px-2 py-2 text-right">Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredEntities.slice(0, 8).map((e) => (
                      <tr key={`human-${e.id}`} className="text-app-text">
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Avatar name={e.name} url={e.avatarUrl ?? null} size={24} />
                            <div className="font-medium truncate">{e.name}</div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 justify-end">
                            <ToneDot pct={e.frAttainmentPct} goal={frGoal} />
                            <span className={attainmentToneClass(e.frAttainmentPct, frGoal)}>
                              {formatPct(e.frAttainmentPct)}
                            </span>
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 justify-end">
                            <ToneDot pct={e.resAttainmentPct} goal={resGoal} />
                            <span className={attainmentToneClass(e.resAttainmentPct, resGoal)}>
                              {formatPct(e.resAttainmentPct)}
                            </span>
                          </span>
                        </td>
                        <td className={`px-2 py-2 text-right ${e.idleBreaches > 0 ? "text-status-warning" : "text-app-muted"}`}>
                          {e.idleBreaches.toLocaleString()}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">{formatSeconds(e.avgFirstResponseSeconds)}</td>
                        <td className="px-2 py-2 text-right">{e.convosHandled.toLocaleString()}</td>
                        <td className="px-2 py-2 text-right">
                          <div style={{ width: 70 }} className="ml-auto">
                            <Sparkline
                              values={e.sparkline ?? []}
                              height={20}
                              variant="line"
                              colorClassName={trendColorClass(e.frAttainmentPct, frGoal)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <footer className="px-5 py-3 border-t border-border">
              <a href={`/analytics/sla/team?range=${range.key}`} className="text-[11px] font-medium text-brand-red hover:underline">
                View full team performance →
              </a>
            </footer>
          </Card>

        </div>

        <div className="text-[11px] text-app-subtle pt-2">All times shown in {timezoneLabel()}.</div>
      </div>
    </AppShell>
  )
}

// ─── Subcomponents ───────────────────────────────────────────────────

function RangeSwitcher({ activeKey, onChange }: { activeKey: RangeKey; onChange: (k: RangeKey) => void }) {
  return (
    <div className="flex items-center gap-1">
      {RANGES.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onChange(r.key)}
          className={
            "rounded-control border px-2.5 py-1 text-[11px] " +
            (r.key === activeKey
              ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
              : "border-border bg-white/5 text-app-muted hover:text-app-text")
          }
        >
          {r.label}
        </button>
      ))}
    </div>
  )
}

function LegendDot({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span className="flex items-center gap-1.5 text-app-muted">
      {dashed ? (
        <svg width="14" height="2" viewBox="0 0 14 2" aria-hidden>
          <line x1="0" y1="1" x2="14" y2="1" stroke={color} strokeWidth="2" strokeDasharray="3 2" />
        </svg>
      ) : (
        <span className="inline-block h-2 w-3 rounded-full" style={{ backgroundColor: color }} />
      )}
      {label}
    </span>
  )
}

function LegendSquare({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-app-muted">
      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
      {label}
    </span>
  )
}

function EmptyState({ height, message }: { height: number; message: string }) {
  return (
    <div className="grid place-items-center text-sm text-app-muted" style={{ height }}>
      {message}
    </div>
  )
}

function BigNumberWithDelta({
  kpi,
  format,
  invertDelta = false,
  showDeltaPct = false,
  priorLabel = "vs prior 7 days",
  className = "",
}: {
  kpi: { value: number | null; delta: number | null; deltaUnit?: "pp" | "sec" | "abs" }
  format: (n: number | null) => string
  invertDelta?: boolean
  showDeltaPct?: boolean
  priorLabel?: string
  className?: string
}) {
  const delta = kpi?.delta ?? null
  const value = kpi?.value ?? null
  const better = invertDelta ? (delta != null && delta < 0) : (delta != null && delta > 0)
  const worse  = invertDelta ? (delta != null && delta > 0) : (delta != null && delta < 0)
  const cls = delta == null || delta === 0
    ? "text-app-muted"
    : better
      ? "text-status-success"
      : worse
        ? "text-status-error"
        : "text-app-muted"
  const arrow = delta == null ? "" : delta > 0 ? "▲" : delta < 0 ? "▼" : "→"
  const abs = delta == null ? null : Math.abs(delta)
  const unitLabel = (() => {
    if (abs == null) return "—"
    const unit = kpi.deltaUnit ?? "abs"
    if (unit === "pp") return `${abs.toFixed(1)}pp`
    if (unit === "sec") return formatSeconds(abs)
    return abs.toLocaleString()
  })()
  let pct: string = ""
  if (showDeltaPct && value != null && delta != null) {
    const prior = value - delta
    if (prior !== 0 && Number.isFinite(delta / Math.abs(prior))) {
      const p = (delta / Math.abs(prior)) * 100
      pct = ` (${p > 0 ? "+" : ""}${p.toFixed(1)}%)`
    }
  }
  return (
    <div className={className}>
      <div className="flex items-baseline gap-2">
        <div className="text-2xl font-semibold tracking-tight text-app-text">{format(value)}</div>
        {delta != null && (
          <span className={`text-xs ${cls}`}>{arrow} {unitLabel}{pct}</span>
        )}
      </div>
      <div className="text-[11px] text-app-subtle mt-0.5">{priorLabel}</div>
    </div>
  )
}

function SortHeader({
  label, active, dir, align, onClick,
}: { label: string; active: boolean; dir: "asc" | "desc"; align: "left" | "right"; onClick: () => void }) {
  const arrow = !active ? "" : dir === "desc" ? "↓" : "↑"
  return (
    <th className={`px-2 py-2 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-app-text ${active ? "text-app-text" : ""}`}
      >
        {label}
        <span className="text-[10px] opacity-70">{arrow}</span>
      </button>
    </th>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────

function entitySortValue(e: EntityRow, key: EntitySortKey): number | string | null {
  switch (key) {
    case "name":                    return e.name.toLowerCase()
    case "frAttainmentPct":         return e.frAttainmentPct
    case "resAttainmentPct":        return e.resAttainmentPct
    case "idleBreaches":            return e.idleBreaches
    case "avgFirstResponseSeconds": return e.avgFirstResponseSeconds
    case "convosHandled":           return e.convosHandled
  }
}

function attainmentToneClass(pct: number | null, goal: number | null): string {
  if (pct == null) return "text-app-muted"
  if (goal == null) return "text-app-text"
  if (pct >= goal) return "text-status-success"
  if (pct >= goal - 5) return "text-status-warning"
  return "text-status-error"
}

function trendColorClass(pct: number | null, goal: number | null): string {
  if (pct == null || goal == null) return "text-app-muted"
  if (pct >= goal) return "text-status-success"
  if (pct >= goal - 5) return "text-status-warning"
  return "text-status-error"
}

// Compact 6px colored dot indicating attainment vs the company goal —
// green ≥ goal, amber within 5pp below, red beyond. Renders nothing
// when there's no data to compare.
function ToneDot({ pct, goal }: { pct: number | null; goal: number | null }) {
  if (pct == null) return null
  let bg = "bg-app-muted"
  if (goal != null) {
    if (pct >= goal) bg = "bg-status-success"
    else if (pct >= goal - 5) bg = "bg-status-warning"
    else bg = "bg-status-error"
  }
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${bg}`} aria-hidden />
}

// "Target ≥ 90%" / "Target ≤ 5m" — with bullseye prefix per the mock.
function targetFootnote(
  goal: number | null | undefined,
  cmp: "gte" | "lte",
  fmt: (n: number | null) => string,
): string {
  if (goal == null) return "No SLA target"
  const op = cmp === "gte" ? "≥" : "≤"
  return `🎯 Target ${op} ${fmt(goal)}`
}

function priorLabelFor(rangeKey: RangeKey): string {
  switch (rangeKey) {
    case "today":  return "vs prior day"
    case "7d":     return "vs prior 7 days"
    case "30d":    return "vs prior 30 days"
    case "90d":    return "vs prior 90 days"
    case "custom": return "vs prior period"
  }
}

function timezoneLabel(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return "UTC"
  }
}

function formatPct(n: number | null): string {
  if (n == null) return "—"
  return `${n.toFixed(1)}%`
}

function formatSeconds(n: number | null): string {
  if (n == null) return "—"
  if (n < 60) return `${Math.round(n)}s`
  const minutes = Math.floor(n / 60)
  const secs = Math.round(n % 60)
  if (minutes < 60) return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

function formatCount(n: number | null): string {
  if (n == null) return "—"
  return n.toLocaleString()
}

function sumArr(xs: number[] | undefined | null): number {
  if (!xs) return 0
  let s = 0
  for (const v of xs) s += v ?? 0
  return s
}
